import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Download, MessageCircle, PenLine } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

export default function Confirmation() {
  const { fmt } = useCurrency();
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const { id } = useParams();
  const [b, setB] = useState(null);
  const canvasRef = useRef(null);
  const agencyCanvasRef = useRef(null);
  const drawing = useRef(false);
  const drawingAgency = useRef(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setB(r.data));
  useEffect(() => { load(); }, [id]);

  const copyRef = () => {
    navigator.clipboard.writeText(b.reference);
    toast.success("Numéro copié");
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem("mbk_token");
    const res = await fetch(`${API_BASE}/bookings/${id}/contract.pdf?lang=${lang}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contrat-${b.reference}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const startDraw = (e) => {
    drawing.current = true;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo((e.touches?.[0]?.clientX || e.clientX) - rect.left, (e.touches?.[0]?.clientY || e.clientY) - rect.top);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.strokeStyle = "#0a1e3f";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineTo((e.touches?.[0]?.clientX || e.clientX) - rect.left, (e.touches?.[0]?.clientY || e.clientY) - rect.top);
    ctx.stroke();
  };
  const stopDraw = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const saveSig = async () => {
    const data = canvasRef.current.toDataURL("image/png");
    await api.post(`/bookings/${id}/sign`, { signature_data: data, signer: "client" });
    toast.success("Contrat signé !");
    load();
  };

  // Agency signature (only for admin/agency_partner)
  const startDrawA = (e) => {
    drawingAgency.current = true;
    const c = agencyCanvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); const rect = c.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo((e.touches?.[0]?.clientX || e.clientX) - rect.left, (e.touches?.[0]?.clientY || e.clientY) - rect.top);
  };
  const drawA = (e) => {
    if (!drawingAgency.current) return;
    const c = agencyCanvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); const rect = c.getBoundingClientRect();
    ctx.strokeStyle = "#0a1e3f"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.lineTo((e.touches?.[0]?.clientX || e.clientX) - rect.left, (e.touches?.[0]?.clientY || e.clientY) - rect.top);
    ctx.stroke();
  };
  const stopDrawA = () => { drawingAgency.current = false; };
  const clearSigA = () => { const c = agencyCanvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); };
  const saveSigA = async () => {
    const c = agencyCanvasRef.current; if (!c) return;
    const data = c.toDataURL("image/png");
    await api.post(`/bookings/${id}/sign`, { signature_data: data, signer: "agency" });
    toast.success("Signature de l'agence enregistrée");
    load();
  };

  if (!b) return <div className="min-h-screen bg-[#f8f9fa]"><SiteHeader /><div className="p-24 text-center">Chargement…</div></div>;

  const pr = b.pricing || {};

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-3xl p-8 border border-gray-100 mbk-card-shadow text-center">
            <div className="h-20 w-20 rounded-full bg-[#dcfce7] text-[#0e7a3f] flex items-center justify-center mx-auto"><CheckCircle2 className="h-10 w-10" /></div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-[#0a1e3f] mt-6">Votre réservation est confirmée !</h1>
            <p className="text-sm text-gray-500 mt-2">Merci pour votre confiance. Un e-mail récapitulatif vous a été envoyé.</p>

            <div className="mt-6 bg-[#dcfce7]/60 rounded-2xl p-4 inline-flex items-center gap-3">
              <div>
                <div className="text-xs text-[#0e7a3f] font-bold uppercase tracking-widest">Numéro de réservation</div>
                <div className="text-2xl font-black text-[#0a1e3f] mt-1" data-testid="booking-ref">{b.reference}</div>
              </div>
              <button onClick={copyRef} className="ml-2 h-9 w-9 rounded-full bg-white hover:bg-gray-50 flex items-center justify-center border border-gray-200"><Copy className="h-4 w-4 text-[#0a1e3f]" /></button>
            </div>

            <div className="mt-4 flex justify-center"><StatusBadge status={b.status} /></div>

            <div className="mt-8 text-left grid grid-cols-1 md:grid-cols-2 gap-4">
              <Info k="Véhicule" v={`${b.vehicle_snapshot?.brand} ${b.vehicle_snapshot?.model}`} />
              <Info k="Agence" v={b.agency_snapshot?.name} />
              <Info k="Dates" v={`Du ${b.start_date} au ${b.end_date}`} />
              <Info k="Lieu de remise" v={b.pickup_location} />
              <Info k="Chauffeur" v={b.with_driver ? "Oui" : "Non"} />
              <Info k="Client" v={`${b.first_name} ${b.last_name}`} />
            </div>

            <div className="mt-8 border-t border-gray-100 pt-6">
              <h3 className="font-display text-lg font-bold text-[#0a1e3f] mb-4 text-left flex items-center gap-2"><PenLine className="h-5 w-5" /> Signature du client</h3>
              {b.client_signature ? (
                <div className="text-left">
                  <img src={b.client_signature.data} alt="signature" className="h-24 border border-gray-200 rounded-xl bg-white" />
                  <div className="text-xs text-gray-500 mt-2">Signé le {new Date(b.client_signature.signed_at).toLocaleString("fr-FR")}</div>
                </div>
              ) : (
                <div>
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={160}
                    className="w-full border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 touch-none"
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                    data-testid="signature-canvas"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={clearSig} data-testid="clear-signature" className="rounded-full">Effacer</Button>
                    <Button onClick={saveSig} className="mbk-btn-green text-white rounded-full" data-testid="save-signature">Signer le contrat</Button>
                  </div>
                </div>
              )}
            </div>

            {(user?.role === "admin" || user?.role === "agency_partner") && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <h3 className="font-display text-lg font-bold text-[#0a1e3f] mb-4 text-left flex items-center gap-2"><PenLine className="h-5 w-5" /> Signature de l'agence</h3>
                {b.agency_signature ? (
                  <div className="text-left">
                    <img src={b.agency_signature.data} alt="signature agence" className="h-24 border border-gray-200 rounded-xl bg-white" />
                    <div className="text-xs text-gray-500 mt-2">Signé le {new Date(b.agency_signature.signed_at).toLocaleString("fr-FR")}</div>
                  </div>
                ) : (
                  <div>
                    <canvas ref={agencyCanvasRef} width={600} height={160}
                      className="w-full border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50 touch-none"
                      onMouseDown={startDrawA} onMouseMove={drawA} onMouseUp={stopDrawA} onMouseLeave={stopDrawA}
                      onTouchStart={startDrawA} onTouchMove={drawA} onTouchEnd={stopDrawA}
                      data-testid="agency-signature-canvas" />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" onClick={clearSigA} className="rounded-full">Effacer</Button>
                      <Button onClick={saveSigA} className="mbk-btn-primary text-white rounded-full" data-testid="save-agency-signature">Valider la signature agence</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button onClick={downloadPdf} data-testid="download-contract" className="mbk-btn-primary text-white rounded-full h-12"><Download className="h-4 w-4 mr-2" />Télécharger le contrat</Button>
              <a href="https://wa.me/242000000000" target="_blank" rel="noreferrer" className="mbk-btn-green text-white rounded-full h-12 inline-flex items-center justify-center font-semibold gap-2" data-testid="whatsapp-link"><MessageCircle className="h-4 w-4" />Contacter par WhatsApp</a>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow h-fit">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Détails du paiement</div>
            <div className="mt-4 space-y-2 text-sm">
              <Row k="Sous-total" v={fmt(pr.subtotal)} />
              <Row k="Acompte payé" v={`-${fmt(pr.deposit)}`} success />
              <div className="pt-2 border-t border-gray-100">
                <Row k="Solde à la remise" v={fmt(pr.balance)} strong />
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const Info = ({ k, v }) => (
  <div className="bg-[#f8f9fa] rounded-xl p-3">
    <div className="text-xs text-gray-500 uppercase tracking-widest font-bold">{k}</div>
    <div className="text-sm font-semibold text-[#0a1e3f] mt-1">{v || "-"}</div>
  </div>
);

const Row = ({ k, v, success, strong }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{k}</span>
    <span className={strong ? "font-black text-[#0a1e3f]" : success ? "font-bold text-[#0e7a3f]" : "font-semibold text-[#0a1e3f]"}>{v}</span>
  </div>
);
